import { createContext, useContext } from 'react';
import type { RegistrationOptions } from './services/webauthn';

export type EnrollmentState =
  | 'BANK_LINKING'
  | 'INITIATING'
  | 'BANK_REDIRECT'
  | 'POLLING'
  | 'READY_TO_AUTH'
  | 'AUTHENTICATING'
  | 'SUBMITTING_AUTH'
  | 'SUCCESS'
  | 'ERROR';

export interface EnrollmentData {
  readonly state: EnrollmentState;
  readonly contract: string;
  readonly cpf: string;
  readonly bankId: string;
  readonly bankName: string;
  readonly bankLogoUrl: string;
  readonly registrationOptions: RegistrationOptions | null | undefined;
  readonly errorMessage: string;
  readonly isAuthError: boolean;
  readonly isTerminalError: boolean;
  readonly intentUrl: string;
}

export type EnrollmentAction =
  | { readonly type: 'SET_CPF'; readonly cpf: string }
  | {
    readonly type: 'SET_BANK';
    readonly bankId: string;
    readonly bankName: string;
    readonly bankLogoUrl: string;
  }
  | { readonly type: 'START_INITIATION' }
  | { readonly type: 'INITIATION_SUCCESS' }
  | { readonly type: 'START_POLLING' }
  | {
    readonly type: 'REGISTRATION_OPTIONS_RECEIVED';
    readonly registrationOptions: RegistrationOptions;
    readonly intentUrl: string;
  }
  | { readonly type: 'START_AUTHENTICATION' }
  | { readonly type: 'START_SUBMITTING_AUTH' }
  | { readonly type: 'ENROLLMENT_SUCCESS'; readonly intentUrl: string }
  | { readonly type: 'SET_ERROR'; readonly errorMessage: string }
  | {
    readonly type: 'SET_TERMINAL_ERROR';
    readonly errorMessage: string;
    readonly intentUrl: string;
  }
  | { readonly type: 'SET_AUTH_ERROR'; readonly errorMessage: string }
  | { readonly type: 'RETRY' }
  | { readonly type: 'RETRY_AUTH' };

interface EnrollmentContextType {
  readonly data: EnrollmentData;
  readonly dispatch: (action: EnrollmentAction) => void;
}

const EnrollmentContext = createContext<EnrollmentContextType | null>(null);

export const EnrollmentProvider = EnrollmentContext.Provider;

export function useEnrollment(): EnrollmentContextType {
  const context = useContext(EnrollmentContext);
  if (!context) {
    throw new Error('EnrollmentProvider not found');
  }
  return context;
}
